import { FC } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { QuestionnaireAnswer, QuestionnaireResponseType } from '@stores/QuestionnaireAdminStore';
import { useQuestionnaireResult } from '@app/view-creator/Admin/QuestionnaireAdminViewCreator';
import { formatPublishedAt } from '@app/pages/admin/AdminQuestionnaires';
import { sittingStatusColors, sittingStatusLabels } from './questionnaireResultsFormatting';
import { ScoreSummary } from './ScoreSummary';

/** One item as the participant saw it: every choice, with theirs filled in. A dropdown shows only their choice. */
const AnswerRow: FC<{ answer: QuestionnaireAnswer }> = ({ answer }) => {
  const answered = answer.freeText !== null || answer.choices.some((choice) => choice.selected);
  const shownChoices =
    answer.responseType === QuestionnaireResponseType.Dropdown
      ? answer.choices.filter((choice) => choice.selected)
      : answer.choices;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {answer.itemCode} v{answer.itemVersion}
      </Typography>
      {answer.leadIn && (
        <Typography variant="body2" color="text.secondary">
          {answer.leadIn}
        </Typography>
      )}
      <Typography variant="body1" sx={{ mb: 1 }}>
        {answer.text}
      </Typography>
      {answer.freeText !== null && <Typography variant="body2">“{answer.freeText}”</Typography>}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {shownChoices.map((choice) => (
          <Chip
            key={choice.id}
            size="small"
            label={choice.scoreValue === null ? choice.text : `${choice.text} (${choice.scoreValue})`}
            color={choice.selected ? 'primary' : 'default'}
            variant={choice.selected ? 'filled' : 'outlined'}
            data-testid={choice.selected ? 'answer-choice-selected' : 'answer-choice'}
          />
        ))}
      </Box>
      {!answered && (
        <Typography variant="body2" color="text.secondary">
          Not answered
        </Typography>
      )}
    </Box>
  );
};

/** One sitting: when it opened and closed, its scores, and each answer with the exact wording the participant saw. */
export const ResultDialog: FC<{ sittingId: number | null; onClose: () => void }> = ({ sittingId, onClose }) => {
  const { data, isLoading, isError } = useQuestionnaireResult(sittingId);
  return (
    <Dialog open={!!sittingId} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{data ? `Participant ${data.cohortUserId}: ${data.formName}` : 'Result'}</DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress />}
        {isError && <Alert severity="error">This result could not be loaded.</Alert>}
        {data && (
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip size="small" label={sittingStatusLabels[data.status]} color={sittingStatusColors[data.status]} />
              <Typography variant="body2" color="text.secondary">
                {data.cohortName} · {data.timepointCode ?? 'Ad hoc'}
                {data.formVersion !== null && ` · version ${data.formVersion}`}
              </Typography>
            </Box>
            <Typography variant="body2">
              Opens {formatPublishedAt(data.availableAt)} ·{' '}
              {data.expiresAt ? `Closes ${formatPublishedAt(data.expiresAt)}` : 'Never closes'}
              {data.completedAt &&
                ` · Completed ${formatPublishedAt(data.completedAt)}${data.completedDay ? ` (day ${data.completedDay})` : ''}`}
            </Typography>
            {data.scores.length > 0 && <ScoreSummary scores={data.scores} />}
            <Divider />
            {data.answers.length ? (
              data.answers.map((answer) => <AnswerRow key={answer.itemCode} answer={answer} />)
            ) : (
              <Typography variant="body2" color="text.secondary">
                The participant hasn&apos;t opened this questionnaire, so there are no questions to show yet.
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
